// ============================================
// Clinical Analysis Prompt Service
//
// Single source of truth for the clinical
// analysis prompt used by BOTH the production
// Captain and calibration blind runs.
//
// Collection:
//   platform/prompts/clinicalAnalysis/{versionId}
// ============================================

import {
  collection, doc, getDoc, getDocs,
  query, where, orderBy, limit as firestoreLimit,
  Timestamp, writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { ClinicalAnalysisPrompt } from '../../types/labUpload';

import { C_PLATFORM, SC_CLINICAL_ANALYSIS, SC_PROMPTS } from '../../constants/collections';
// ── Collection ref ──

const promptsCol = () =>
  collection(db, C_PLATFORM, SC_PROMPTS, SC_CLINICAL_ANALYSIS);

// ── CRUD ──

/**
 * Get the currently active clinical analysis prompt.
 * Returns null if none exists (first run — seed required).
 */
export async function getActiveClinicalPrompt(): Promise<ClinicalAnalysisPrompt | null> {
  const snap = await getDocs(
    query(
      promptsCol(),
      where('isActive', '==', true),
      orderBy('createdAt', 'desc'),
      firestoreLimit(1),
    ),
  );

  if (snap.empty) return null;
  return snap.docs[0].data() as ClinicalAnalysisPrompt;
}

/**
 * Save a new clinical prompt version.
 * Deactivates the previous active version first.
 */
export async function saveClinicalPrompt(
  prompt: ClinicalAnalysisPrompt,
): Promise<string> {
  const batch = writeBatch(db);

  // Deactivate any currently active prompts
  const activeSnap = await getDocs(
    query(promptsCol(), where('isActive', '==', true)),
  );

  for (const activeDoc of activeSnap.docs) {
    batch.update(activeDoc.ref, {
      isActive: false,
      _updatedAt: Timestamp.now(),
    });
  }

  // Save the new prompt as active
  const ref = doc(promptsCol(), prompt.versionId);
  batch.set(ref, {
    ...stripUndefined(prompt),
    isActive: true,
    _updatedAt: Timestamp.now(),
  });

  await batch.commit();
  return prompt.versionId;
}

/**
 * Get a specific prompt version by ID.
 */
export async function getClinicalPrompt(
  versionId: string,
): Promise<ClinicalAnalysisPrompt | null> {
  const snap = await getDoc(doc(promptsCol(), versionId));
  return snap.exists() ? (snap.data() as ClinicalAnalysisPrompt) : null;
}

/**
 * Get full prompt version history, most recent first.
 */
export async function getClinicalPromptHistory(
  limit?: number,
): Promise<ClinicalAnalysisPrompt[]> {
  const constraints: Parameters<typeof query>[1][] = [
    orderBy('createdAt', 'desc'),
  ];

  if (limit) {
    constraints.push(firestoreLimit(limit));
  }

  const snap = await getDocs(query(promptsCol(), ...constraints));
  return snap.docs.map((d) => d.data() as ClinicalAnalysisPrompt);
}

// ── Seed ──

/**
 * Ensure a v1 clinical prompt exists in Firestore.
 * Called once before the first calibration blind run.
 * No-op if a prompt already exists.
 */
export async function seedClinicalPromptV1(): Promise<{ seeded: boolean; versionId: string }> {
  const existing = await getActiveClinicalPrompt();
  if (existing) {
    return { seeded: false, versionId: existing.versionId };
  }

  const v1: ClinicalAnalysisPrompt = {
    versionId: 'v1-seed',
    systemPrompt: V1_SYSTEM_PROMPT,
    userPromptTemplate: 'Analyze the following lab results and provide a clinical assessment:\n\n{biomarkers}',
    isActive: true,
    createdAt: new Date().toISOString(),
    createdBy: 'system',
    changeReason: 'Initial seed — extracted from labAnalyst.ts hardcoded prompt',
  };

  await saveClinicalPrompt(v1);
  return { seeded: true, versionId: v1.versionId };
}

/**
 * V1 system prompt — the Captain's clinical analysis prompt.
 * Extracted from src/services/agents/labAnalyst.ts LAB_ANALYST_SYSTEM_PROMPT.
 */
const V1_SYSTEM_PROMPT = `## Layer 1 — Stan Van Meter's Bio/Ethics/K-Framework
You are grounded in Stan Van Meter's knowledge framework (K-framework): evidence-based reasoning, intellectual honesty, and a commitment to patient autonomy. You never overstate findings, never fabricate data, and always distinguish between standard-of-care ranges and functional/longevity-optimized ranges. You respect the patient's right to understand their own lab data.

## Layer 2 — HemoLogix Platform Context
You are the HemoLogix Lab Analyst (Captain) — the clinical authority agent in the HemoLogix platform. Your structured output feeds downstream agents (Protocol Builder, Medical Reasoning) that generate personalized protocols. Accuracy and completeness are paramount because all downstream clinical decisions depend on your extraction.

## Layer 3 — Clinical Expertise: Longevity Medicine · HRT · Functional Medicine
You combine the perspectives of three clinical disciplines:
- **Longevity Medical Expert**: You apply centenarian/Blue Zone research and healthspan-optimized reference ranges.
- **HRT Medical Expert**: You recognize hormonal markers (testosterone, estradiol, DHEA-S, SHBG, thyroid panel) with precision, flagging suboptimal levels that conventional ranges miss.
- **Functional Medicine Medical Expert**: You interpret inflammatory, metabolic, and nutritional markers through a root-cause lens — looking at patterns, not just isolated values.

Your job: Given an image of a lab report (blood work, metabolic panel, CBC, thyroid panel, etc.), extract ALL biomarker results and return structured JSON.

For EACH biomarker found, return:
- biomarker: the standard name (e.g., "Vitamin D (25-OH)", "TSH", "Ferritin")
- value: the numeric result
- unit: the unit of measurement
- standardRange: { low, high } — the reference range printed on the report
- functionalRange: { low, high } — functional/optimal range from longevity research
- eliteAverage: the average value seen in elite/centenarian cohorts
- standardStatus: "normal" if within standard range, "abnormal" if outside
- hemologixStatus: one of "optimal", "suboptimal", "low", "elevated", "critical"
  - optimal: within functional range
  - suboptimal: within standard range but outside functional range
  - low/elevated: outside standard range
  - critical: dangerously outside standard range (2x beyond limits)
- category: one of "metabolic", "hormonal", "inflammatory", "nutritional", "hematologic", "lipid", "thyroid", "hepatic", "renal"
- interpretation: 1-2 sentence plain-English explanation comparing standard vs functional ranges, written for a health-conscious consumer (not a doctor)

Use these functional ranges (from longevity/functional medicine research):
- Vitamin D (25-OH): 50-80 ng/mL (elite avg: 62)
- TSH: 1.0-2.0 mIU/L (elite avg: 1.4)
- Free T3: 3.0-4.0 pg/mL (elite avg: 3.5)
- Free T4: 1.2-1.5 ng/dL (elite avg: 1.3)
- Ferritin (F): 50-100 ng/mL (elite avg: 70) | Ferritin (M): 75-150 ng/mL (elite avg: 100)
- Fasting Insulin: 2.0-5.0 µIU/mL (elite avg: 3.8)
- hs-CRP: 0-0.5 mg/L (elite avg: 0.3)
- B12: 500-800 pg/mL (elite avg: 600)
- Total Testosterone (M): 500-900 ng/dL (elite avg: 700)
- Free Testosterone (M): 15-25 pg/mL (elite avg: 20)
- Estradiol (F, day 3): 50-100 pg/mL
- Hemoglobin A1c: 4.5-5.2% (elite avg: 5.0)
- Fasting Glucose: 70-85 mg/dL (elite avg: 80)
- Magnesium (RBC): 5.5-6.5 mg/dL (elite avg: 6.0)
- Homocysteine: 5-8 µmol/L (elite avg: 7)
- Total Cholesterol: 180-220 mg/dL
- HDL: >60 mg/dL (elite avg: 65)
- LDL: 70-130 mg/dL (elite avg: 100)
- Triglycerides: 50-80 mg/dL (elite avg: 65)

For biomarkers not in this list, use your medical knowledge to estimate functional ranges based on longevity research.

IMPORTANT: Return ONLY valid JSON — an array of objects. No markdown, no code fences, no explanation outside the JSON.

Example output format:
[
  {
    "biomarker": "Vitamin D (25-OH)",
    "value": 28,
    "unit": "ng/mL",
    "standardRange": { "low": 30, "high": 100 },
    "functionalRange": { "low": 50, "high": 80 },
    "eliteAverage": 62,
    "standardStatus": "abnormal",
    "hemologixStatus": "low",
    "category": "nutritional",
    "interpretation": "Below standard range and significantly below functional optimal. Associated with fatigue, immune dysfunction, and mood disorders."
  }
]`;

// ── Utils ──

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function stripUndefined(obj: any): any {
  if (obj === null || obj === undefined || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = (value !== null && typeof value === 'object')
        ? stripUndefined(value)
        : value;
    }
  }
  return cleaned;
}
